import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Badge from 'kingpin/atoms/Badge';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import useMetricOptions from 'hooks/useMetricOptions';
import { listAgentCheckRuns } from 'services/agentKb';

import { relativeTime } from '../constants';
import { AccountKnowledgeContext } from '../AccountKnowledgeProvider';
import { AgentEvalContext } from './AgentEvalProvider';
import {
  productLabel,
  requirementLabel,
  RUN_TARGET_LABELS,
  VERDICT_BADGES,
  VERDICT_LABELS,
  verdictOf,
} from './constants';
import { buildHintSection } from './buildHintSection';
import { isCheckHinted } from './isCheckHinted';
import EvalRunResultView from './EvalRunResultView';

// The detail pane for one check: what it asks, what it asserts, and what the
// agent last did with it on this account.

const DetailDiv = styled.div`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 20px 32px 40px 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Eyebrow = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const EmptyDiv = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const EvalDetailEmpty = () => (
  <EmptyDiv data-testid="agent-eval-detail-empty">
    <Typography.Body type="Body 13" color="var(--color-text-secondary)">
      Select a check to see what the agent did with it.
    </Typography.Body>
  </EmptyDiv>
);

const EvalDetail = ({
  check,
  target,
  onEdit,
}: {
  check: AgentEval.ListedCheck;
  target: AgentEval.RunTarget;
  onEdit: () => void;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { api } = useContext(CloudFunctionClientContext);
  const { runs, runningIds, runOne, rememberRun, rights, archiveCheck } =
    useContext(AgentEvalContext);
  const { sections } = useContext(AccountKnowledgeContext);
  const { metricOptionsLookup } = useMetricOptions();

  const [isFetching, setIsFetching] = useState<boolean>(false);
  const run = runs[check.id];
  const isRunning = runningIds.has(check.id);
  const verdict = verdictOf(check);

  // The head only carries the summary; pull the last full run on selection.
  useEffect(() => {
    let isActive = true;
    if (run || !check.lastRun) {
      return undefined;
    }
    setIsFetching(true);
    listAgentCheckRuns(api, { checkId: check.id, limit: 1 }).then((result) => {
      if (!isActive) {
        return;
      }
      setIsFetching(false);
      if (result.ok && result.data.runs.length > 0) {
        rememberRun(result.data.runs[0]);
      }
    });
    return () => {
      isActive = false;
    };
  }, [api, check.id, check.lastRun, run, rememberRun]);

  const metricName = (id: string): string =>
    metricOptionsLookup[id]?.name ?? id;

  // Teaching the agent is the only path that puts a check's intent in front of
  // it — a check itself never injects.
  const teach = () => {
    const metricId = check.expected.metricId;
    if (!metricId) {
      return;
    }
    const prefill = buildHintSection({
      check,
      expectedMetric: { id: metricId, name: metricName(metricId) },
      forbiddenMetrics: (check.expected.forbiddenMetricIds ?? []).map((id) => ({
        id,
        name: metricName(id),
      })),
    });
    const marker = '/insights/agent';
    const base = location.pathname.slice(
      0,
      location.pathname.indexOf(marker) + marker.length,
    );
    navigate(`${base}/account`, { state: { prefill } });
  };

  return (
    <DetailDiv data-testid={`agent-eval-detail-${check.id}`}>
      <Eyebrow>
        <Typography.Body type="Body 12" color="var(--color-text-secondary)">
          {[
            check.products.map(productLabel).join(' · ') || 'Any product',
            check.id,
            requirementLabel(check.requires),
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography.Body>
      </Eyebrow>

      <Typography.Header type="H3">{check.question}</Typography.Header>

      {check.purpose !== '' && (
        <Typography.Body type="Body 13" color="var(--color-text-secondary)">
          {check.purpose}
        </Typography.Body>
      )}

      <Row centerAlign spaceBetween>
        <Row centerAlign style={{ gap: 8, flexWrap: 'wrap' }}>
          <Badge
            badgeType={VERDICT_BADGES[verdict]}
            text={VERDICT_LABELS[verdict]}
          />
          {isCheckHinted({ check, sections }) && (
            <Badge badgeType="Blue" text="Hinted" />
          )}
          {check.lastRun && (
            <Typography.Body type="Body 12" color="var(--color-text-secondary)">
              {RUN_TARGET_LABELS[check.lastRun.target]} ·{' '}
              {check.lastRun.kbVersion ?? 'draft'} ·{' '}
              {relativeTime(check.lastRun.ranOn)}
            </Typography.Body>
          )}
        </Row>
        <Row centerAlign style={{ gap: 8 }}>
          <Button
            type="Primary"
            size="Small"
            label={isRunning ? 'Running…' : check.lastRun ? 'Re-run' : 'Run'}
            isDisabled={isRunning || !rights.canEdit}
            testId={`agent-eval-run-${check.id}`}
            onClick={() => runOne({ checkId: check.id, target })}
          />
          <Button
            type="Ghost"
            size="Small"
            label="Edit"
            isDisabled={!rights.canEdit}
            testId={`agent-eval-edit-${check.id}`}
            onClick={onEdit}
          />
          <Button
            type="Ghost"
            size="Small"
            label="Teach the agent"
            isDisabled={!check.expected.metricId}
            testId={`agent-eval-teach-${check.id}`}
            onClick={teach}
          />
          <Button
            type="Ghost"
            size="Small"
            label="Archive"
            isDisabled={!rights.canEdit}
            testId={`agent-eval-archive-${check.id}`}
            onClick={() => archiveCheck(check.id)}
          />
        </Row>
      </Row>

      {check.expected.metricId && (
        <Typography.Body type="Body 12" color="var(--color-text-secondary)">
          Expects {metricName(check.expected.metricId)}
          {check.expected.forbiddenMetricIds?.length
            ? `, not ${check.expected.forbiddenMetricIds
                .map(metricName)
                .join(', ')}`
            : ''}
        </Typography.Body>
      )}

      {!check.lastRun && (
        <Typography.Body type="Body 13" color="var(--color-text-secondary)">
          Not run on this account yet.
        </Typography.Body>
      )}
      {isFetching && (
        <Typography.Body type="Body 13" color="var(--color-text-secondary)">
          Loading the last run…
        </Typography.Body>
      )}
      {run && <EvalRunResultView run={run} />}
    </DetailDiv>
  );
};

export default EvalDetail;
